import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import PropTypes from "prop-types";

/**
 * Breadcrumbs component - Shows current navigation path
 * Provides context to users about their location in the app
 */
const BreadcrumbsBase = ({ location, category }) => {

  // Map of paths to readable labels for WATERPROOF
  const pathLabels = useMemo(() => ({
    // Puntos de Venta
    '/machines': 'ONEs',
    '/minis': 'MINIs',
    
    // Operativa
    '/incidences': 'Incidencias',
    '/audit': 'Audit',
    '/modules': 'Módulos',
    '/matrixs': 'Versiones',
    '/tpvs': 'TPVs',
    
    // Ventas
    '/sales': 'Análisis',
    '/arqueos': 'Gráficos',
    '/arqueossales': 'KPI',
    '/objectives': 'Objetivos',
    '/comissions': 'Comisiones',
    '/quicksight': 'QuickSight',
    
    // Fichajes
    '/shifts': 'Jornada Promotor',
    
    // Administración
    '/customers': 'Clientes',
    '/contracts': 'Contratos',
    '/promoters': 'Promotores',
    '/users': 'Trabajadores',
    '/logistics': 'Logística',
  }), []);

  // Map of paths to their parent categories
  const pathCategories = useMemo(() => ({
    // Puntos de Venta
    '/machines': 'Puntos de Venta',
    '/minis': 'Puntos de Venta',
    
    // Operativa
    '/incidences': 'Operativa',
    '/audit': 'Operativa',
    '/modules': 'Operativa',
    '/matrixs': 'Operativa',
    '/tpvs': 'Operativa',
    
    // Ventas
    '/sales': 'Ventas',
    '/arqueos': 'Ventas',
    '/arqueossales': 'Ventas',
    '/objectives': 'Ventas',
    '/comissions': 'Ventas',
    '/quicksight': 'Ventas',
    
    // Fichajes
    '/shifts': 'Fichajes',
    '/shiftsadmin': 'Fichajes',
    
    // Administración
    '/customers': 'Administración',
    '/contracts': 'Administración',
    '/promoters': 'Administración',
    '/users': 'Administración',
    '/logistics': 'Administración',
  }), []);

  // Build breadcrumb items from current path
  const breadcrumbs = useMemo(() => {
    const pathname = location?.pathname || '/';
    const items = [];

    // Always add home
    items.push({
      label: 'Inicio',
      path: '/',
      isHome: true,
    });

    // Check if we're on home page
    if (pathname === '/') {
      return items;
    }

    // Get base path (without ID parameters)
    const pathParts = pathname.split('/').filter(Boolean);
    const basePath = `/${pathParts[0]}`;
    
    // Add category if exists
    const categoryName = pathCategories[basePath];
    if (categoryName) {
      items.push({
        label: categoryName,
        path: null, // Categories are not clickable
        isCategory: true,
      });
    }

    // Add current page
    const pageLabel = pathLabels[basePath];
    if (pageLabel) {
      items.push({
        label: pageLabel,
        path: basePath,
        isPage: true,
      });
    }

    // If there's an ID in the URL (like /machines/123)
    if (pathParts.length > 1) {
      const id = pathParts[pathParts.length - 1];
      // Only show if it looks like an ID (not another route segment)
      if (id.length > 3 && !/^[a-z]+$/.test(id)) {
        items.push({
          label: 'Detalle',
          path: pathname,
          isDetail: true,
        });
      }
    }

    return items;
  }, [location, pathLabels, pathCategories]);

  // Don't render if we only have home (we're on home page)
  if (breadcrumbs.length <= 1) {
    return null;
  }

  return (
    <nav 
      className="breadcrumbs" 
      aria-label="Breadcrumb"
      style={{
        background: 'var(--bg-secondary)',
        borderBottom: '1px solid var(--border-color)',
        padding: '12px 24px',
        position: 'sticky',
        top: '64px',
        zIndex: 100
      }}
    >
      <ol 
        className="breadcrumbs-list"
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '8px',
          listStyle: 'none',
          margin: 0,
          padding: 0
        }}
      >
        {breadcrumbs.map((crumb, index) => {
          const isLast = index === breadcrumbs.length - 1;

          return (
            <li 
              key={index} 
              className="breadcrumbs-item"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {crumb.path && !isLast ? (
                <Link 
                  to={crumb.path} 
                  className="breadcrumbs-link"
                  aria-label={`Ir a ${crumb.label}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '14px',
                    color: 'var(--text-secondary)',
                    textDecoration: 'none',
                    transition: 'color 0.15s ease, transform 0.15s ease'
                  }}
                >
                  {crumb.isHome && (
                    <svg 
                      width="14" 
                      height="14" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2"
                      style={{ flexShrink: 0 }}
                    >
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                      <polyline points="9 22 9 12 15 12 15 22"/>
                    </svg>
                  )}
                  <span>{crumb.label}</span>
                </Link>
              ) : (
                <span 
                  className={`breadcrumbs-text ${isLast ? 'breadcrumbs-current' : ''}`}
                  aria-current={isLast ? 'page' : undefined}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '14px',
                    color: isLast ? 'var(--text-primary)' : 'var(--text-secondary)',
                    fontWeight: isLast ? 500 : 'normal'
                  }}
                >
                  {crumb.isHome && (
                    <svg 
                      width="14" 
                      height="14" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2"
                      style={{ flexShrink: 0 }}
                    >
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                      <polyline points="9 22 9 12 15 12 15 22"/>
                    </svg>
                  )}
                  <span>{crumb.label}</span>
                </span>
              )}
              {!isLast && (
                <svg 
                  width="14" 
                  height="14" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                  className="breadcrumbs-separator"
                  aria-hidden="true"
                  style={{
                    color: 'var(--border-color)',
                    flexShrink: 0
                  }}
                >
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

BreadcrumbsBase.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string
  }),
  category: PropTypes.string,
};

const mapStateToProps = (state) => ({
  location: state.router ? state.router.location : { pathname: '/' },
  category: state.auth ? state.auth.category : null,
});

const Breadcrumbs = connect(mapStateToProps)(BreadcrumbsBase);

export default Breadcrumbs;
